import React, { useMemo } from "react";
import {
  StandardTable,
  StandardTableColumn,
} from "../../common/StandardTable/StandardTable";
import { StaffIdentity } from "../../common/StaffIdentity";
import { StaffNotificationPreferences } from "../../../types/notificationPreferences";
import NotificationPreferenceCell, {
  NotificationPreferenceCellProps,
} from "./NotificationPreferenceCell";
import NotificationRowLabel from "./NotificationRowLabel";
import { PreferenceRow } from "./preferenceRows";

const LABEL_COLUMN_WIDTH = 240;
const STAFF_COLUMN_WIDTH = 220;

/** Profile details the preferences API doesn't carry (from the salon's staff list). */
export interface StaffProfileDetails {
  photoUrl?: string | null;
  title?: string | null;
}

export interface NotificationPreferencesLayoutProps extends Pick<
  NotificationPreferenceCellProps,
  "editing" | "onPreferenceChange" | "onEmailOptInChange"
> {
  rows: PreferenceRow[];
  staff: StaffNotificationPreferences[];
  staffDetails: Record<string, StaffProfileDetails>;
}

/**
 * Staff header shared by the matrix columns and the mobile cards: avatar,
 * name and title (when set) stacked vertically. No role here.
 */
export const renderStaffIdentity = (
  member: StaffNotificationPreferences,
  details: StaffProfileDetails | undefined,
) => (
  <StaffIdentity
    layout="vertical"
    firstName={member.first_name}
    lastName={member.last_name}
    title={details?.title}
    photoUrl={details?.photoUrl}
  />
);

/**
 * Desktop layout: notification types down the side, one column per staff
 * member. Scrolls horizontally once there are more staff than fit.
 */
export const NotificationPreferencesMatrix: React.FC<
  NotificationPreferencesLayoutProps
> = ({
  rows,
  staff,
  staffDetails,
  editing,
  onPreferenceChange,
  onEmailOptInChange,
}) => {
  const columns = useMemo<StandardTableColumn<PreferenceRow>[]>(
    () => [
      {
        key: "notification",
        width: LABEL_COLUMN_WIDTH,
        render: (row) => <NotificationRowLabel row={row} />,
      },
      ...staff.map(
        (member): StandardTableColumn<PreferenceRow> => ({
          key: member.staff_user_id,
          align: "center",
          width: STAFF_COLUMN_WIDTH,
          label: renderStaffIdentity(
            member,
            staffDetails[member.staff_user_id],
          ),
          render: (row) => (
            <NotificationPreferenceCell
              row={row}
              staff={member}
              editing={editing}
              onPreferenceChange={onPreferenceChange}
              onEmailOptInChange={onEmailOptInChange}
            />
          ),
        }),
      ),
    ],
    [staff, staffDetails, editing, onPreferenceChange, onEmailOptInChange],
  );

  return (
    <StandardTable
      ariaLabel="Notification settings by staff member"
      columns={columns}
      data={rows}
      getRowKey={(row) => row.key}
      minWidth={LABEL_COLUMN_WIDTH + staff.length * STAFF_COLUMN_WIDTH}
    />
  );
};

export default NotificationPreferencesMatrix;
